import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yapNumberWhen, yapStringWhen, yupNumberString } from '../common';
import { AddressInfo } from 'views/templates/module/addressInput/hook';
import { RelastionShip } from 'views/utils/enums';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';

export enum AboutRulerType {
  None = 0,
  Same = 1,
  Different = 2,
}

export enum RulerImportantPublicWorkerType {
  None = 0,
  Yes = 1,
  No = 2,
}

export enum RelastionShipType {
  None = 0,
  Half = 1,
  Quarter = 2,
  JigyoKatsudo = 3,
  Daihyosya = 4,
}

export type RulerInfoType = {
  aboutRuler1: AboutRulerType;
  addtionalRuler2: boolean;
  addtionalRuler3: boolean;
  ruler1PostCode: string;
  ruler1Prefecture: string;
  ruler1CityCountry: string;
  ruler1TownStreetAddress: string;
  hasNoRuler1TownStreetAddress: boolean;
  ruler1BuildingNameAndRoomNumber: string;
  ruler1Relationship: RelastionShipType;
  ruler1Name: string;
  ruler1BirthDay: Date | null;
  ruler1MailAddress: string;
  ruler1ImportantPublicWorker: RulerImportantPublicWorkerType;
  ruler1ConfirmMailAddress: string;
  aboutRuler2: AboutRulerType;
  ruler2PostCode: string;
  ruler2Prefecture: string;
  ruler2CityCountry: string;
  ruler2TownStreetAddress: string;
  hasNoRuler2TownStreetAddress: boolean;
  ruler2BuildingNameAndRoomNumber: string;
  ruler2Relationship: RelastionShipType;
  ruler2Name: string;
  ruler2BirthDay: Date | null;
  ruler2MailAddress: string;
  ruler2ConfirmMailAddress: string;
  ruler2ImportantPublicWorker: RulerImportantPublicWorkerType;
  ruler3PostCode: string;
  ruler3Prefecture: string;
  ruler3CityCountry: string;
  ruler3TownStreetAddress: string;
  hasNoRuler3TownStreetAddress: boolean;
  aboutRuler3: AboutRulerType;
  ruler3BuildingNameAndRoomNumber: string;
  ruler3Relationship: RelastionShipType;
  ruler3Name: string;
  ruler3BirthDay: Date | null;
  ruler3MailAddress: string;
  ruler3ConfirmMailAddress: string;
  ruler3ImportantPublicWorker: RulerImportantPublicWorkerType;
};

export const useRulerInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({
    aboutRuler1: Yup.number().not([AboutRulerType.None], ErrorWord.SELECT),
    ruler1Relationship: Yup.string().required(ErrorWord.SELECT),
    ruler1PostCode: yapNumberWhen(
      'aboutRuler1',
      (val: AboutRulerType) => val === AboutRulerType.Different,
      ErrorWord.POSTCODE,
      7,
      false,
    ),
    ruler1Prefecture: yapStringWhen(
      'aboutRuler1',
      (val: AboutRulerType) => val === AboutRulerType.Different,
      ErrorWord.SELECT,
    ),
    ruler1CityCountry: yapStringWhen(
      'aboutRuler1',
      (val: AboutRulerType) => val === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(40, getMaxLengthErrorMessage(40)),
    ruler1TownStreetAddress: yapStringWhen(
      ['aboutRuler1', 'hasNoRuler1TownStreetAddress'],
      (confirm, hasNo) => confirm === AboutRulerType.Different && !hasNo,
      ErrorWord.INPUT,
    ).max(255, getMaxLengthErrorMessage(255)),
    ruler1BuildingNameAndRoomNumber:Yup.string().max(50, getMaxLengthErrorMessage(50)),
    ruler1Name: yapStringWhen(
      'aboutRuler1',
      (val: AboutRulerType) => val === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(80, getMaxLengthErrorMessage(80)),
    ruler1BirthDay: Yup.date().when('aboutRuler1', {
      is: (val: AboutRulerType) => val === AboutRulerType.Different,
      then: (schema) => schema.required(ErrorWord.Date),
      otherwise: (schema) => schema.strip(),
    }),
    ruler1MailAddress: Yup.string().when('aboutRuler1', {
      is: (val: AboutRulerType) => val === AboutRulerType.Different,
      then: (schema) => schema.trim().nullable().email(ErrorWord.EMAIL),
      otherwise: (schema) => schema.strip(),
    }).max(80, getMaxLengthErrorMessage(80)),
    ruler1ConfirmMailAddress: Yup.string().when(['aboutRuler1', 'ruler1MailAddress'], {
      is: (val: AboutRulerType, mail: string) => val === AboutRulerType.Different && mail,
      then: (schema) =>
        schema.required(ErrorWord.EMAIL_CONFIRM).oneOf([Yup.ref('ruler1MailAddress')], ErrorWord.EMAIL_CONFIRM),
      otherwise: (schema) => schema.strip(),
    }),
    ruler1ImportantPublicWorker: Yup.number().not([RulerImportantPublicWorkerType.None], ErrorWord.SELECT),
    aboutRuler2: Yup.number().when(['addtionalRuler2'], {
      is: (addtional: boolean) => addtional,
      then: (schema) => schema.not([AboutRulerType.None], ErrorWord.SELECT),
      otherwise: (schema) => schema.strip(),
    }),
    ruler2Relationship: yapStringWhen('addtionalRuler2', (val: boolean) => val, ErrorWord.SELECT),
    ruler2PostCode: Yup.string().when(['addtionalRuler2', 'aboutRuler2'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.required(ErrorWord.POSTCODE).matches(/^[0-9]{7,7}$/gi, ErrorWord.POSTCODE),
      otherwise: (schema) => schema.strip(),
    }),
    ruler2Prefecture: yapStringWhen(
      ['addtionalRuler2', 'aboutRuler2'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.SELECT,
    ),
    ruler2CityCountry: yapStringWhen(
      ['addtionalRuler2', 'aboutRuler2'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(40, getMaxLengthErrorMessage(40)),
    ruler2TownStreetAddress: yapStringWhen(
      ['addtionalRuler2', 'aboutRuler2', 'hasNoRuler2TownStreetAddress'],
      (addtional, about, hasNo) => addtional && about === AboutRulerType.Different && !hasNo,
      ErrorWord.INPUT,
    ).max(255, getMaxLengthErrorMessage(255)),
    ruler2BuildingNameAndRoomNumber:Yup.string().max(50, getMaxLengthErrorMessage(50)),
    ruler2Name: yapStringWhen(
      ['addtionalRuler2', 'aboutRuler2'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(80, getMaxLengthErrorMessage(80)),
    ruler2BirthDay: Yup.date().when(['addtionalRuler2', 'aboutRuler2'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.required(ErrorWord.Date),
      otherwise: (schema) => schema.strip(),
    }),
    ruler2MailAddress: Yup.string().when(['addtionalRuler2', 'aboutRuler2'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.trim().nullable().email(ErrorWord.EMAIL),
      otherwise: (schema) => schema.strip(),
    }).max(80, getMaxLengthErrorMessage(80)),
    ruler2ConfirmMailAddress: Yup.string().when(['addtionalRuler2', 'aboutRuler2', 'ruler2MailAddress'], {
      is: (addtional: boolean, about: AboutRulerType, mail: string) =>
        addtional && about === AboutRulerType.Different && mail,
      then: (schema) =>
        schema.required(ErrorWord.EMAIL_CONFIRM).oneOf([Yup.ref('ruler2MailAddress')], ErrorWord.EMAIL_CONFIRM),
      otherwise: (schema) => schema.strip(),
    }),
    ruler2ImportantPublicWorker: Yup.number().when(['addtionalRuler2'], {
      is: (addtional: boolean) => addtional,
      then: (schema) => schema.not([RulerImportantPublicWorkerType.None], ErrorWord.SELECT),
      otherwise: (schema) => schema.strip(),
    }),
    aboutRuler3: Yup.number().when(['addtionalRuler3'], {
      is: (addtional: boolean) => addtional,
      then: (schema) => schema.not([AboutRulerType.None], ErrorWord.SELECT),
      otherwise: (schema) => schema.strip(),
    }),
    ruler3Relationship: yapStringWhen('addtionalRuler3', (val: boolean) => val, ErrorWord.SELECT),
    ruler3PostCode: Yup.string().when(['addtionalRuler3', 'aboutRuler3'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.required(ErrorWord.POSTCODE).matches(/^[0-9]{7,7}$/gi, ErrorWord.POSTCODE),
      otherwise: (schema) => schema.strip(),
    }),
    ruler3Prefecture: yapStringWhen(
      ['addtionalRuler3', 'aboutRuler3'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.SELECT,
    ),
    ruler3CityCountry: yapStringWhen(
      ['addtionalRuler3', 'aboutRuler3'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(40, getMaxLengthErrorMessage(40)),
    ruler3TownStreetAddress: yapStringWhen(
      ['addtionalRuler3', 'aboutRuler3', 'hasNoRuler3TownStreetAddress'],
      (addtional, about, hasNo) => addtional && about === AboutRulerType.Different && !hasNo,
      ErrorWord.INPUT,
    ).max(255, getMaxLengthErrorMessage(255)),
    ruler3BuildingNameAndRoomNumber:Yup.string().max(50, getMaxLengthErrorMessage(50)),
    ruler3Name: yapStringWhen(
      ['addtionalRuler3', 'aboutRuler3'],
      (addtional, about) => addtional && about === AboutRulerType.Different,
      ErrorWord.INPUT,
    ).max(80, getMaxLengthErrorMessage(80)),
    ruler3BirthDay: Yup.date().when(['addtionalRuler3', 'aboutRuler3'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.required(ErrorWord.Date),
      otherwise: (schema) => schema.strip(),
    }),
    ruler3MailAddress: Yup.string().when(['addtionalRuler3', 'aboutRuler3'], {
      is: (addtional: boolean, about: AboutRulerType) => addtional && about === AboutRulerType.Different,
      then: (schema) => schema.trim().nullable().email(ErrorWord.EMAIL),
      otherwise: (schema) => schema.strip(),
    }).max(80, getMaxLengthErrorMessage(80)),
    ruler3ConfirmMailAddress: Yup.string().when(['addtionalRuler3', 'aboutRuler3', 'Ruler3MailAddress'], {
      is: (addtional: boolean, about: AboutRulerType, mail: string) =>
        addtional && about === AboutRulerType.Different && mail,
      then: (schema) =>
        schema.required(ErrorWord.EMAIL_CONFIRM).oneOf([Yup.ref('Ruler3MailAddress')], ErrorWord.EMAIL_CONFIRM),
      otherwise: (schema) => schema.strip(),
    }),
    ruler3ImportantPublicWorker: Yup.number().when(['addtionalRuler3'], {
      is: (addtional: boolean) => addtional,
      then: (schema) => schema.not([RulerImportantPublicWorkerType.None], ErrorWord.SELECT),
      otherwise: (schema) => schema.strip(),
    }),
  });

  const initialValues: RulerInfoType = {
    aboutRuler1: param.aboutRuler1 ?? AboutRulerType.None,
    addtionalRuler2: param.addtionalRuler2 ?? false,
    addtionalRuler3: param.addtionalRuler3 ?? false,
    ruler1PostCode: param.ruler1PostCode ?? '',
    ruler1Prefecture: param.ruler1Prefecture ?? '',
    ruler1CityCountry: param.ruler1CityCountry ?? '',
    ruler1TownStreetAddress: param.ruler1TownStreetAddress ?? '',
    hasNoRuler1TownStreetAddress: param.hasNoRuler1TownStreetAddress ?? false,
    ruler1BuildingNameAndRoomNumber: param.ruler1BuildingNameAndRoomNumber ?? '',
    ruler1Relationship: param.ruler1Relationship ?? null,
    ruler1Name: param.ruler1Name,
    ruler1BirthDay: param.ruler1BirthDay ?? null,
    ruler1MailAddress: param.ruler1MailAddress,
    ruler1ConfirmMailAddress: param.ruler1ConfirmMailAddress,
    ruler1ImportantPublicWorker: param.ruler1ImportantPublicWorker ?? RulerImportantPublicWorkerType.None,
    aboutRuler2: param.aboutRuler2 ?? AboutRulerType.None,
    ruler2PostCode: param.ruler2PostCode ?? '',
    ruler2Prefecture: param.ruler2Prefecture ?? '',
    ruler2CityCountry: param.ruler2CityCountry ?? '',
    ruler2TownStreetAddress: param.ruler2TownStreetAddress ?? '',
    hasNoRuler2TownStreetAddress: param.hasNoRuler2TownStreetAddress ?? false,
    ruler2BuildingNameAndRoomNumber: param.ruler2BuildingNameAndRoomNumber ?? '',
    ruler2Relationship: param.ruler2Relationship ?? null,
    ruler2Name: param.ruler2Name,
    ruler2BirthDay: param.ruler2BirthDay ?? null,
    ruler2MailAddress: param.ruler2MailAddress,
    ruler2ConfirmMailAddress: param.ruler2ConfirmMailAddress,
    ruler2ImportantPublicWorker: param.ruler2ImportantPublicWorker ?? RulerImportantPublicWorkerType.None,
    aboutRuler3: param.aboutRuler3 ?? AboutRulerType.None,
    ruler3PostCode: param.ruler3PostCode ?? '',
    ruler3Prefecture: param.ruler3Prefecture ?? '',
    ruler3CityCountry: param.ruler3CityCountry ?? '',
    ruler3TownStreetAddress: param.ruler3TownStreetAddress ?? '',
    hasNoRuler3TownStreetAddress: param.hasNoRuler3TownStreetAddress ?? false,
    ruler3BuildingNameAndRoomNumber: param.ruler3BuildingNameAndRoomNumber ?? '',
    ruler3Relationship: param.ruler3Relationship ?? null,
    ruler3Name: param.ruler3Name,
    ruler3BirthDay: param.ruler3BirthDay ?? null,
    ruler3MailAddress: param.ruler3MailAddress,
    ruler3ConfirmMailAddress: param.ruler3ConfirmMailAddress,
    ruler3ImportantPublicWorker: param.ruler3ImportantPublicWorker ?? RulerImportantPublicWorkerType.None,
  };

  const formik = useFormik<RulerInfoType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.investorCategoryInfo);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  const ruler1Props = {
    aboutRulerProps: {
      selected: formik.values.aboutRuler1,
      onChange: (value: AboutRulerType) => {
        formik.setFieldValue('aboutRuler1', value);
        if (value === AboutRulerType.Same) {
          formik.values.addtionalRuler2 && formik.setFieldValue('aboutRuler2', AboutRulerType.Different);
          formik.values.addtionalRuler3 && formik.setFieldValue('aboutRuler3', AboutRulerType.Different);

          // 条件(実質的支配者についてが代表者とイコールでない場合)により入力されていた情報を消す
          formik.setFieldValue('ruler1Name', '');
          formik.setFieldValue('ruler1BirthDay', '');
          formik.setFieldValue('ruler1MailAddress', '');
          formik.setFieldValue('ruler1ConfirmMailAddress', '');
          formik.setFieldValue('ruler1PostCode', '');
          formik.setFieldValue('ruler1Prefecture', '');
          formik.setFieldValue('ruler1CityCountry', '');
          formik.setFieldValue('hasNoRuler1TownStreetAddress', false);
          formik.setFieldValue('ruler1TownStreetAddress', '');
          formik.setFieldValue('ruler1BuildingNameAndRoomNumber', '');
        } else if (formik.values.ruler1Relationship === RelastionShipType.Daihyosya) {
          formik.setFieldValue('ruler1Relationship', '');
        }
      },
      disabled: formik.values.aboutRuler2 === AboutRulerType.Same || formik.values.aboutRuler3 === AboutRulerType.Same,
      error: formik.errors.aboutRuler1,
    },
    relationshipProps: {
      selected: formik.values.ruler1Relationship,
      onChange: (value: RelastionShip) => formik.setFieldValue('ruler1Relationship', value),
      error: formik.errors.ruler1Relationship,
    },
    nameProps: {
      value: formik.values.ruler1Name,
      onChange: (value: string) => formik.setFieldValue('ruler1Name', value),
      error: formik.errors.ruler1Name,
    },
    birthDayProps: {
      date: formik.values.ruler1BirthDay,
      name: 'ruler1BirthDay',
      minDate: new Date('1900/1/1'),
      maxDate: new Date(),
      onChange: (value: any) => formik.setFieldValue('ruler1BirthDay', value),
      onBlur: formik.handleBlur,
      isWeekEndDisable: false,
      error: formik.errors.ruler1BirthDay,
    },
    mailAddressProps: {
      value: formik.values.ruler1MailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler1MailAddress', value),
      error: formik.errors.ruler1MailAddress,
    },
    confirmMailAddressProps: {
      value: formik.values.ruler1ConfirmMailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler1ConfirmMailAddress', value),
      error: formik.errors.ruler1ConfirmMailAddress,
    },
    addressProps: {
      name: 'ruler1_address',
      postCode: {
        value: formik.values.ruler1PostCode,
        onChange: (value: string) => {
          formik.setFieldValue('ruler1PostCode', value);
        },
        error: formik.errors.ruler1PostCode,
      },
      prefecture: {
        selectedOption: formik.values.ruler1Prefecture,
        setSelectedOption: (value: string) => formik.setFieldValue('ruler1Prefecture', value),
        error: formik.errors.ruler1Prefecture,
      },
      municipality: {
        value: formik.values.ruler1CityCountry,
        onChange: (value: string) => {
          formik.setFieldValue('ruler1CityCountry', value);
        },
        error: formik.errors.ruler1CityCountry,
      },
      streetAddress: {
        value: formik.values.ruler1TownStreetAddress,
        onChange: (value: string) => {
          formik.setFieldValue('ruler1TownStreetAddress', value);
        },
        error: formik.errors.ruler1TownStreetAddress,
        disabled: formik.values.hasNoRuler1TownStreetAddress,
      },
      hasNoTownStreetAddress: {
        checked: formik.values.hasNoRuler1TownStreetAddress,
        setChecked: (value: boolean) => {
          formik.setFieldValue('ruler1TownStreetAddress', '');
          formik.setFieldValue('hasNoRuler1TownStreetAddress', value);
        },
      },
      buildingNameAndRoomNumber: {
        value: formik.values.ruler1BuildingNameAndRoomNumber,
        onChange: (value: string) => formik.setFieldValue('ruler1BuildingNameAndRoomNumber', value),
        error: formik.errors.ruler1BuildingNameAndRoomNumber,
      },
      onSearchAddress: (addressInfo: AddressInfo) => {
        formik.setFieldValue('ruler1Prefecture', addressInfo.prefecture);
        formik.setFieldValue('ruler1CityCountry', addressInfo.cityCountry);
        formik.setFieldValue('hasNoRuler1TownStreetAddress', false);
        formik.setFieldValue('ruler1TownStreetAddress', addressInfo.townStreetAddress);
        formik.setFieldValue('ruler1BuildingNameAndRoomNumber', '');
      },
    },
    importantPublicWorker: {
      selected: formik.values.ruler1ImportantPublicWorker,
      onChange: (value: RulerImportantPublicWorkerType) => {
        formik.setFieldValue('ruler1ImportantPublicWorker', value)
      },
      error: formik.errors.ruler1ImportantPublicWorker,
    },
  };

  const ruler2Props = {
    aboutRulerProps: {
      selected: formik.values.aboutRuler2,
      onChange: (value: AboutRulerType) => {
        formik.setFieldValue('aboutRuler2', value);
        if (value === AboutRulerType.Same) {
          formik.setFieldValue('aboutRuler1', AboutRulerType.Different);
          formik.values.addtionalRuler3 && formik.setFieldValue('aboutRuler3', AboutRulerType.Different);

          // 条件(実質的支配者についてが代表者とイコールでない場合)により入力されていた情報を消す
          formik.setFieldValue('ruler2Name', '');
          formik.setFieldValue('ruler2BirthDay', '');
          formik.setFieldValue('ruler2MailAddress', '');
          formik.setFieldValue('ruler2ConfirmMailAddress', '');
          formik.setFieldValue('ruler2PostCode', '');
          formik.setFieldValue('ruler2Prefecture', '');
          formik.setFieldValue('ruler2CityCountry', '');
          formik.setFieldValue('hasNoRuler2TownStreetAddress', false);
          formik.setFieldValue('ruler2TownStreetAddress', '');
          formik.setFieldValue('ruler2BuildingNameAndRoomNumber', '');
        } else if (formik.values.ruler2Relationship === RelastionShipType.Daihyosya) {
          formik.setFieldValue('ruler2Relationship', '');
        }
      },
      disabled: formik.values.aboutRuler1 === AboutRulerType.Same || formik.values.aboutRuler3 === AboutRulerType.Same,
      error: formik.errors.aboutRuler2,
    },
    relationshipProps: {
      selected: formik.values.ruler2Relationship,
      onChange: (value: RelastionShip) => formik.setFieldValue('ruler2Relationship', value),
      error: formik.errors.ruler2Relationship,
    },
    nameProps: {
      value: formik.values.ruler2Name,
      onChange: (value: string) => formik.setFieldValue('ruler2Name', value),
      error: formik.errors.ruler2Name,
    },
    birthDayProps: {
      date: formik.values.ruler2BirthDay,
      name: 'ruler1BirthDay',
      minDate: new Date('1900/1/1'),
      maxDate: new Date(),
      onChange: (value: any) => formik.setFieldValue('ruler2BirthDay', value),
      onBlur: formik.handleBlur,
      isWeekEndDisable: false,
      error: formik.errors.ruler2BirthDay,
    },
    mailAddressProps: {
      value: formik.values.ruler2MailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler2MailAddress', value),
      error: formik.errors.ruler2MailAddress,
    },
    confirmMailAddressProps: {
      value: formik.values.ruler2ConfirmMailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler2ConfirmMailAddress', value),
      error: formik.errors.ruler2ConfirmMailAddress,
    },
    addressProps: {
      name: 'ruler2_address',
      postCode: {
        value: formik.values.ruler2PostCode,
        onChange: (value: string) => formik.setFieldValue('ruler2PostCode', value),
        error: formik.errors.ruler2PostCode,
      },
      prefecture: {
        selectedOption: formik.values.ruler2Prefecture,
        setSelectedOption: (value: string) => formik.setFieldValue('ruler2Prefecture', value),
        error: formik.errors.ruler2Prefecture,
      },
      municipality: {
        value: formik.values.ruler2CityCountry,
        onChange: (value: string) => {
          formik.setFieldValue('ruler2CityCountry', value);
        },
        error: formik.errors.ruler2CityCountry,
      },
      streetAddress: {
        value: formik.values.ruler2TownStreetAddress,
        onChange: (value: string) => {
          formik.setFieldValue('ruler2TownStreetAddress', value);
        },
        error: formik.errors.ruler2TownStreetAddress,
        disabled: formik.values.hasNoRuler2TownStreetAddress,
      },
      hasNoTownStreetAddress: {
        checked: formik.values.hasNoRuler2TownStreetAddress,
        setChecked: (value: boolean) => {
          formik.setFieldValue('ruler2TownStreetAddress', '');
          formik.setFieldValue('hasNoRuler2TownStreetAddress', value);
        },
      },
      buildingNameAndRoomNumber: {
        value: formik.values.ruler2BuildingNameAndRoomNumber,
        onChange: (value: string) => formik.setFieldValue('ruler2BuildingNameAndRoomNumber', value),
        error: formik.errors.ruler2BuildingNameAndRoomNumber,
      },
      onSearchAddress: (addressInfo: AddressInfo) => {
        formik.setFieldValue('ruler2Prefecture', addressInfo.prefecture);
        formik.setFieldValue('ruler2CityCountry', addressInfo.cityCountry);
        formik.setFieldValue('hasNoRuler2TownStreetAddress', false);
        formik.setFieldValue('ruler2TownStreetAddress', addressInfo.townStreetAddress);
        formik.setFieldValue('ruler2BuildingNameAndRoomNumber', '');
      },
    },
    importantPublicWorker: {
      selected: formik.values.ruler2ImportantPublicWorker,
      onChange: (value: RulerImportantPublicWorkerType) => {
        formik.setFieldValue('ruler2ImportantPublicWorker', value)
      },
      error: formik.errors.ruler2ImportantPublicWorker,

    },
  };

  const ruler3Props = {
    aboutRulerProps: {
      selected: formik.values.aboutRuler3,
      onChange: (value: AboutRulerType) => {
        formik.setFieldValue('aboutRuler3', value);
        if (value === AboutRulerType.Same) {
          formik.setFieldValue('aboutRuler1', AboutRulerType.Different);
          formik.values.addtionalRuler2 && formik.setFieldValue('aboutRuler2', AboutRulerType.Different);

          // 条件(実質的支配者についてが代表者とイコールでない場合)により入力されていた情報を消す
          formik.setFieldValue('ruler3Name', '');
          formik.setFieldValue('ruler3BirthDay', '');
          formik.setFieldValue('ruler3MailAddress', '');
          formik.setFieldValue('ruler3ConfirmMailAddress', '');
          formik.setFieldValue('ruler3PostCode', '');
          formik.setFieldValue('ruler3Prefecture', '');
          formik.setFieldValue('ruler3CityCountry', '');
          formik.setFieldValue('hasNoRuler3TownStreetAddress', false);
          formik.setFieldValue('ruler3TownStreetAddress', '');
          formik.setFieldValue('ruler3BuildingNameAndRoomNumber', '');
        } else if (formik.values.ruler3Relationship === RelastionShipType.Daihyosya) {
          formik.setFieldValue('ruler3Relationship', '');
        }
      },
      disabled: formik.values.aboutRuler1 === AboutRulerType.Same || formik.values.aboutRuler2 === AboutRulerType.Same,
      error: formik.errors.aboutRuler3,
    },
    relationshipProps: {
      selected: formik.values.ruler3Relationship,
      onChange: (value: RelastionShip) => formik.setFieldValue('ruler3Relationship', value),
      error: formik.errors.ruler3Relationship,
    },
    nameProps: {
      value: formik.values.ruler3Name,
      onChange: (value: string) => formik.setFieldValue('ruler3Name', value),
      error: formik.errors.ruler3Name,
    },
    birthDayProps: {
      date: formik.values.ruler3BirthDay,
      name: 'ruler1BirthDay',
      minDate: new Date('1900/1/1'),
      maxDate: new Date(),
      onChange: (value: any) => formik.setFieldValue('ruler3BirthDay', value),
      onBlur: formik.handleBlur,
      isWeekEndDisable: false,
      error: formik.errors.ruler3BirthDay,
    },
    mailAddressProps: {
      value: formik.values.ruler3MailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler3MailAddress', value),
      error: formik.errors.ruler3MailAddress,
    },
    confirmMailAddressProps: {
      value: formik.values.ruler3ConfirmMailAddress,
      onChange: (value: string) => formik.setFieldValue('ruler3ConfirmMailAddress', value),
      error: formik.errors.ruler3ConfirmMailAddress,
    },
    addressProps: {
      name: 'ruler3_address',
      postCode: {
        value: formik.values.ruler3PostCode,
        onChange: (value: string) => {
          formik.setFieldValue('ruler3PostCode', value);
        },
        error: formik.errors.ruler3PostCode,
      },
      prefecture: {
        selectedOption: formik.values.ruler3Prefecture,
        setSelectedOption: (value: string) => formik.setFieldValue('ruler3Prefecture', value),
        error: formik.errors.ruler3Prefecture,
      },
      municipality: {
        value: formik.values.ruler3CityCountry,
        onChange: (value: string) => {
          formik.setFieldValue('ruler3CityCountry', value);
        },
        error: formik.errors.ruler3CityCountry,
      },
      streetAddress: {
        value: formik.values.ruler3TownStreetAddress,
        onChange: (value: string) => {
          formik.setFieldValue('ruler3TownStreetAddress', value);
        },
        error: formik.errors.ruler3TownStreetAddress,
        disabled: formik.values.hasNoRuler3TownStreetAddress,
      },
      hasNoTownStreetAddress: {
        checked: formik.values.hasNoRuler3TownStreetAddress,
        setChecked: (value: boolean) => {
          formik.setFieldValue('ruler3TownStreetAddress', '');
          formik.setFieldValue('hasNoRuler3TownStreetAddress', value);
        },
      },
      buildingNameAndRoomNumber: {
        value: formik.values.ruler3BuildingNameAndRoomNumber,
        onChange: (value: string) => formik.setFieldValue('ruler3BuildingNameAndRoomNumber', value),
        error: formik.errors.ruler3BuildingNameAndRoomNumber,
      },
      onSearchAddress: (addressInfo: AddressInfo) => {
        formik.setFieldValue('ruler3Prefecture', addressInfo.prefecture);
        formik.setFieldValue('ruler3CityCountry', addressInfo.cityCountry);
        formik.setFieldValue('hasNoRuler3TownStreetAddress', false);
        formik.setFieldValue('ruler3TownStreetAddress', addressInfo.townStreetAddress);
        formik.setFieldValue('ruler3BuildingNameAndRoomNumber', '');
      },
    },
    importantPublicWorker: {
      selected: formik.values.ruler3ImportantPublicWorker,
      onChange: (value: RulerImportantPublicWorkerType) => {
        formik.setFieldValue('ruler3ImportantPublicWorker', value)
      },
      error: formik.errors.ruler3ImportantPublicWorker,
    },
  };

  const addRuler2 = {
    onClick: () => {
      if (formik.values.addtionalRuler2) {

        // 削除
        formik.setFieldValue('aboutRuler2',  formik.values.aboutRuler3 ?? AboutRulerType.None);
        formik.setFieldValue('ruler2PostCode', formik.values.ruler3PostCode ?? '');
        formik.setFieldValue('ruler2Prefecture', formik.values.ruler3Prefecture ?? '');
        formik.setFieldValue('ruler2CityCountry', formik.values.ruler3CityCountry ?? '');
        formik.setFieldValue('ruler2TownStreetAddress', formik.values.ruler3TownStreetAddress ?? '');
        formik.setFieldValue('hasNoRuler2TownStreetAddress', formik.values.hasNoRuler3TownStreetAddress ?? false);
        formik.setFieldValue('ruler2BuildingNameAndRoomNumber', formik.values.ruler3BuildingNameAndRoomNumber ?? '');
        formik.setFieldValue('ruler2Relationship', formik.values.ruler3Relationship ?? null);
        formik.setFieldValue('ruler2Name', formik.values.ruler3Name ?? '');
        formik.setFieldValue('ruler2BirthDay', formik.values.ruler3BirthDay ?? null);
        formik.setFieldValue('ruler2MailAddress', formik.values.ruler3MailAddress ?? '');
        formik.setFieldValue('ruler2ConfirmMailAddress', formik.values.ruler3ConfirmMailAddress ?? '');
        formik.setFieldValue('ruler2ImportantPublicWorker', formik.values.ruler3ImportantPublicWorker ?? RulerImportantPublicWorkerType.None);

        //実質的支配者3が入力されている場合
        if(formik.values.addtionalRuler3){
          addRuler3.onClick();
          return ;
        }         
      } else {
        // 追加
        formik.setFieldValue(
          'aboutRuler2',
          formik.values.aboutRuler1 === AboutRulerType.Same || formik.values.aboutRuler3 === AboutRulerType.Same
            ? AboutRulerType.Different
            : AboutRulerType.None,
        );
      }
      formik.setFieldValue('addtionalRuler2', !formik.values.addtionalRuler2);     
    },
  };

  const addRuler3 = {
    onClick: () => {
      if (formik.values.addtionalRuler3) {
        // 削除
        formik.setFieldValue('aboutRuler3', AboutRulerType.None);
        formik.setFieldValue('ruler3PostCode', '');
        formik.setFieldValue('ruler3Prefecture', '');
        formik.setFieldValue('ruler3CityCountry', '');
        formik.setFieldValue('ruler3TownStreetAddress', '');
        formik.setFieldValue('hasNoRuler3TownStreetAddress', false);
        formik.setFieldValue('ruler3BuildingNameAndRoomNumber', '');
        formik.setFieldValue('ruler3Relationship', null);
        formik.setFieldValue('ruler3Name', '');
        formik.setFieldValue('ruler3BirthDay', null);
        formik.setFieldValue('ruler3MailAddress', '');
        formik.setFieldValue('ruler3ConfirmMailAddress', '');
        formik.setFieldValue('ruler3ImportantPublicWorker', RulerImportantPublicWorkerType.None);
      } else {
        // 追加
        formik.setFieldValue(
          'aboutRuler3',
          formik.values.aboutRuler1 === AboutRulerType.Same || formik.values.aboutRuler2 === AboutRulerType.Same
            ? AboutRulerType.Different
            : AboutRulerType.None,
        );
      }
      formik.setFieldValue('addtionalRuler3', !formik.values.addtionalRuler3);
    },
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    ruler1Props,
    addRuler2,
    ruler2Props,
    addRuler3,
    ruler3Props,
  };
};
